import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Loader2, AlertCircle, BarChart3, RefreshCw, Users, CheckCircle,
    Activity, MessageSquare, Bot, TrendingUp, X, ExternalLink, ChevronDown, ChevronUp,
    Smartphone, Monitor,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchChatbotAnalytics, fetchChatbotAnalyticsSessions } from '../api/analyticsApi';
import { syncVirtualContact } from '../../../api/contact';

const StatCard = ({ icon: Icon, label, value, sub, onClick }) => {
    const clickable = typeof onClick === 'function';
    return (
        <div
            className={`bg-white rounded-2xl shadow-sm border border-gray-100 p-5 transition ${
                clickable ? 'cursor-pointer hover:shadow-md hover:border-[#55c5d0]/40 hover:bg-gray-50/50' : ''
            }`}
            onClick={onClick}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
        >
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white bg-[#55c5d0]">
                    <Icon className="w-5 h-5" />
                </div>
                <div>
                    <div className="text-sm text-gray-500">{label}</div>
                    <div className="text-2xl font-semibold text-slate-900">{value ?? '—'}</div>
                    {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
                    {clickable && <div className="text-[10px] text-[#55c5d0] mt-1 font-medium">Click to view details</div>}
                </div>
            </div>
        </div>
    );
};

const roleLabel = (role) => {
    if (role === 'bot') return 'GeeVee';
    if (role === 'agent') return 'Agent';
    if (role === 'user') return 'Buyer';
    return role;
};

const SessionMessages = ({ messages }) => {
    if (!messages?.length) {
        return <p className="text-sm text-gray-400 italic">No messages recorded.</p>;
    }
    return (
        <div className="space-y-2 max-h-72 overflow-y-auto">
            {messages.map((msg) => (
                <div
                    key={msg.id}
                    className={`text-sm rounded-lg px-3 py-2 ${
                        msg.role === 'user'
                            ? 'bg-white border border-gray-200 ml-6'
                            : msg.role === 'agent'
                            ? 'bg-blue-50 border border-blue-100 mr-6'
                            : 'bg-[#55c5d0]/10 border border-[#55c5d0]/20 mr-6'
                    }`}
                >
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">
                        {roleLabel(msg.role)}
                    </div>
                    <div className="text-gray-800 whitespace-pre-wrap">{msg.content}</div>
                </div>
            ))}
        </div>
    );
};

const ChatbotSessionsDrilldownModal = ({
    title,
    sessions,
    loading,
    onClose,
    onOpenProfile,
    redirecting,
    initialExpandedId = null,
}) => {
    const [expandedId, setExpandedId] = useState(initialExpandedId);

    useEffect(() => {
        setExpandedId(initialExpandedId);
    }, [initialExpandedId, sessions]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b border-gray-200">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {loading ? 'Loading…' : `${sessions.length} conversation${sessions.length !== 1 ? 's' : ''}`}
                            {!loading && ' · Expand a row to read the chat · Open profile to see all convos on their CRM record'}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>
                <div className="p-5 overflow-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Loading conversations…
                        </div>
                    ) : sessions.length === 0 ? (
                        <p className="text-sm text-gray-500">No conversations in this period.</p>
                    ) : (
                        <div className="space-y-2">
                            {sessions.map((session) => {
                                const expanded = expandedId === session.id;
                                const displayName = session.contact_name || session.user_name || 'Anonymous visitor';
                                const canOpenProfile = session.contact_id || session.buyer_id;

                                return (
                                    <div key={session.id} className="border border-gray-100 rounded-xl overflow-hidden">
                                        <div className="flex items-start gap-3 p-4 hover:bg-gray-50">
                                            <button
                                                type="button"
                                                onClick={() => setExpandedId(expanded ? null : session.id)}
                                                className="flex-1 flex items-start justify-between gap-3 text-left min-w-0"
                                            >
                                                <div className="min-w-0">
                                                    <div className="font-semibold text-gray-900 text-sm truncate">
                                                        {displayName}
                                                        <span className="text-gray-400 font-normal ml-2">#{session.id}</span>
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-0.5">
                                                        {(session.user_email || 'No email')} · {session.flow_label || session.flow} · {session.status}
                                                    </div>
                                                    <div className="text-xs text-gray-400 mt-0.5">
                                                        {session.message_count} messages
                                                        {session.created_at && ` · ${new Date(session.created_at).toLocaleString('en-AU')}`}
                                                    </div>
                                                    {session.summary && (
                                                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">{session.summary}</p>
                                                    )}
                                                </div>
                                                {expanded ? (
                                                    <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
                                                ) : (
                                                    <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
                                                )}
                                            </button>
                                            {canOpenProfile && (
                                                <button
                                                    type="button"
                                                    disabled={redirecting}
                                                    onClick={() => onOpenProfile(session)}
                                                    className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-[#55c5d0] border border-[#55c5d0]/30 rounded-lg hover:bg-[#55c5d0]/5 disabled:opacity-50"
                                                >
                                                    <ExternalLink className="w-3.5 h-3.5" />
                                                    Profile
                                                </button>
                                            )}
                                        </div>
                                        {expanded && (
                                            <div className="border-t border-gray-100 bg-gray-50 p-4">
                                                <SessionMessages messages={session.messages} />
                                                {session.lead_id && (
                                                    <p className="text-xs text-emerald-600 font-medium mt-3">Linked to Lead #{session.lead_id}</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const CARD_FILTERS = {
    total: { filter: 'all', title: 'All chat sessions' },
    completed: { filter: 'completed', title: 'Completed conversations' },
    messages: { filter: 'all', title: 'All conversations (by messages)' },
    leads: { filter: 'with_leads', title: 'Conversations that generated leads' },
    buyer_portal: { filter: 'buyer_portal', title: 'GeeVee (Buyer Portal) conversations' },
    messenger: { filter: 'messenger', title: 'Messenger threads' },
    unique_users: { filter: 'unique_users', title: 'Unique chat users' },
    qualification: { filter: 'with_transcript', title: 'Sessions with qualification scores' },
    recent: { filter: 'all', title: 'Recent conversations' },
};

const ChatbotAnalyticsPanel = ({ days = 30, beforeRecent = null }) => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [drilldown, setDrilldown] = useState(null);
    const [drilldownSessions, setDrilldownSessions] = useState([]);
    const [drilldownLoading, setDrilldownLoading] = useState(false);
    const [redirecting, setRedirecting] = useState(false);

    const load = async () => {
        setLoading(true);
        setErrorMsg('');
        try {
            const payload = await fetchChatbotAnalytics(days);
            setData(payload || {});
        } catch (e) {
            const detail = e.response?.data?.detail || e.message || 'Failed to load chatbot analytics.';
            setErrorMsg(detail);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days]);

    useEffect(() => {
        if (!drilldown) {
            setDrilldownSessions([]);
            return undefined;
        }

        let cancelled = false;
        setDrilldownLoading(true);
        setDrilldownSessions([]);

        fetchChatbotAnalyticsSessions(days, drilldown.filter, true, 200)
            .then((res) => {
                if (cancelled) return;
                let sessions = res?.sessions || [];
                if (drilldown.key === 'recent') {
                    sessions = sessions.slice(0, 10);
                }
                setDrilldownSessions(sessions);
            })
            .catch(() => {
                if (!cancelled) toast.error('Could not load conversation details.');
            })
            .finally(() => {
                if (!cancelled) setDrilldownLoading(false);
            });

        return () => { cancelled = true; };
    }, [drilldown, days]);

    const openDrilldown = (key, initialExpandedId = null) => {
        const config = CARD_FILTERS[key];
        if (!config) return;
        setDrilldown({ key, ...config, initialExpandedId });
    };

    const openProfile = async (session) => {
        if (redirecting) return;
        setRedirecting(true);
        const name = session.contact_name || session.user_name || 'buyer';
        const toastId = toast.loading(`Opening ${name}…`);
        try {
            let contactId = session.contact_id;
            if (!contactId && session.buyer_id) {
                const response = await syncVirtualContact(`buyer_${session.buyer_id}`);
                contactId = response?.data?.id;
            }
            if (!contactId) throw new Error('Could not resolve contact');
            toast.dismiss(toastId);
            setDrilldown(null);
            navigate(`/contacts/${contactId}?tab=conversations`);
        } catch {
            toast.error('Could not open buyer profile.', { id: toastId });
        } finally {
            setRedirecting(false);
        }
    };

    const flowRows = Array.isArray(data?.flow_breakdown) ? data.flow_breakdown : [];
    const portalRows = Array.isArray(data?.portal_breakdown) ? data.portal_breakdown : [];
    const chatEventRows = data?.chat_events?.breakdown || [];
    const recentSessions = data?.recent_sessions || [];
    const geeveeByDevice = data?.geevee_by_device || { mobile: 0, desktop: 0, unknown: 0 };
    const geeveeDeviceTotal = (geeveeByDevice.mobile || 0)
        + (geeveeByDevice.desktop || 0)
        + (geeveeByDevice.unknown || 0);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white bg-[#55c5d0]">
                        <Bot className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">Chatbot Analytics</h3>
                        <p className="text-sm text-gray-500">GeeVee AI, website chatbot, and messenger conversations</p>
                    </div>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="px-4 py-2 rounded-xl text-white flex items-center gap-2 text-sm font-medium bg-[#55c5d0] hover:bg-[#48b7c3] disabled:opacity-50"
                >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Refresh
                </button>
            </div>

            {errorMsg && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    <span>{errorMsg}</span>
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <StatCard
                    icon={Users}
                    label="Total Sessions"
                    value={data?.sessions?.total}
                    onClick={(data?.sessions?.total > 0) ? () => openDrilldown('total') : undefined}
                />
                <StatCard
                    icon={CheckCircle}
                    label="Completed"
                    value={data?.sessions?.completed}
                    sub={`${data?.sessions?.completion_rate ?? 0}% completion`}
                    onClick={(data?.sessions?.completed > 0) ? () => openDrilldown('completed') : undefined}
                />
                <StatCard
                    icon={MessageSquare}
                    label="Messages"
                    value={data?.messages?.total}
                    sub={`${data?.messages?.avg_per_session ?? 0} avg / session`}
                    onClick={(data?.messages?.total > 0) ? () => openDrilldown('messages') : undefined}
                />
                <StatCard
                    icon={TrendingUp}
                    label="Leads Generated"
                    value={data?.leads?.total_generated}
                    sub={`${data?.leads?.conversion_rate ?? 0}% conversion`}
                    onClick={(data?.leads?.total_generated > 0) ? () => openDrilldown('leads') : undefined}
                />
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h4 className="font-semibold text-gray-900 mb-1">GeeVee sessions by device</h4>
                <p className="text-xs text-gray-500 mb-4">
                    Buyer Portal chat starts in this range — mobile vs desktop from event tracking.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <StatCard
                        icon={Smartphone}
                        label="Mobile"
                        value={geeveeByDevice.mobile ?? 0}
                        sub={geeveeDeviceTotal > 0
                            ? `${Math.round(((geeveeByDevice.mobile || 0) / geeveeDeviceTotal) * 100)}% of tracked`
                            : 'No sessions yet'}
                    />
                    <StatCard
                        icon={Monitor}
                        label="Desktop"
                        value={geeveeByDevice.desktop ?? 0}
                        sub={geeveeDeviceTotal > 0
                            ? `${Math.round(((geeveeByDevice.desktop || 0) / geeveeDeviceTotal) * 100)}% of tracked`
                            : 'No sessions yet'}
                    />
                    <StatCard
                        icon={Activity}
                        label="Unknown device"
                        value={geeveeByDevice.unknown ?? 0}
                        sub="Legacy events before device tracking"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <StatCard
                    icon={Bot}
                    label="Buyer Portal (GeeVee)"
                    value={data?.buyer_portal?.total_sessions}
                    sub={`${data?.buyer_portal?.completion_rate ?? 0}% completed`}
                    onClick={(data?.buyer_portal?.total_sessions > 0) ? () => openDrilldown('buyer_portal') : undefined}
                />
                <StatCard
                    icon={Activity}
                    label="Messenger Threads"
                    value={data?.messenger_sessions}
                    onClick={(data?.messenger_sessions > 0) ? () => openDrilldown('messenger') : undefined}
                />
                <StatCard
                    icon={Users}
                    label="Unique Chat Users"
                    value={data?.chat_events?.unique_users}
                    onClick={(data?.chat_events?.unique_users > 0) ? () => openDrilldown('unique_users') : undefined}
                />
                <StatCard
                    icon={BarChart3}
                    label="Avg Qualification"
                    value={data?.quality?.avg_qualification_score}
                    sub={`${data?.quality?.avg_duration_minutes ?? 0} min avg`}
                    onClick={(data?.quality?.avg_qualification_score > 0) ? () => openDrilldown('qualification') : undefined}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                    <h4 className="font-semibold text-gray-900 mb-4">Sessions by Flow</h4>
                    {flowRows.length === 0 ? (
                        <p className="text-sm text-gray-400">No session data yet.</p>
                    ) : (
                        <div className="space-y-2">
                            {flowRows.map((row) => (
                                <div key={row.flow} className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600 capitalize">{row.flow?.replace('_', ' ')}</span>
                                    <span className="font-semibold text-gray-900">{row.count}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                    <h4 className="font-semibold text-gray-900 mb-4">Sessions by Portal</h4>
                    {portalRows.length === 0 ? (
                        <p className="text-sm text-gray-400">No portal data yet.</p>
                    ) : (
                        <div className="space-y-2">
                            {portalRows.map((row) => (
                                <div key={row.portal_type} className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600 capitalize">{row.portal_type?.replace('_', ' ')}</span>
                                    <span className="font-semibold text-gray-900">{row.count}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {chatEventRows.length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                    <h4 className="font-semibold text-gray-900 mb-4">Buyer Portal Chat Events</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {chatEventRows.map((row) => (
                            <div key={row.event_name} className="bg-gray-50 rounded-xl px-4 py-3">
                                <div className="text-xs text-gray-500 capitalize">{row.event_name?.replace(/_/g, ' ')}</div>
                                <div className="text-xl font-bold text-gray-900">{row.count}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Caller-supplied section (e.g. GeeVee Question Categories) rendered
                just above Recent Conversations so Recent Conversations stays last. */}
            {beforeRecent}

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-900">Recent Conversations</h4>
                    {recentSessions.length > 0 && (
                        <button
                            type="button"
                            onClick={() => openDrilldown('recent')}
                            className="text-xs font-semibold text-[#55c5d0] hover:underline"
                        >
                            View all
                        </button>
                    )}
                </div>
                {recentSessions.length === 0 ? (
                    <p className="text-sm text-gray-400">No recent chat sessions.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-gray-500 border-b">
                                    <th className="pb-2 pr-4">User</th>
                                    <th className="pb-2 pr-4">Flow</th>
                                    <th className="pb-2 pr-4">Status</th>
                                    <th className="pb-2 pr-4">Messages</th>
                                    <th className="pb-2">Started</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentSessions.map((s) => (
                                    <tr
                                        key={s.id}
                                        className="border-b border-gray-50 cursor-pointer hover:bg-gray-50"
                                        onClick={() => openDrilldown('total', s.id)}
                                    >
                                        <td className="py-2 pr-4">
                                            <div className="font-medium text-gray-900">
                                                {s.contact_name || s.user_name || 'Anonymous'}
                                            </div>
                                            {s.user_email && (
                                                <div className="text-xs text-gray-400">{s.user_email}</div>
                                            )}
                                        </td>
                                        <td className="py-2 pr-4 capitalize">{s.flow?.replace('_', ' ')}</td>
                                        <td className="py-2 pr-4 capitalize">{s.status}</td>
                                        <td className="py-2 pr-4">{s.message_count ?? '—'}</td>
                                        <td className="py-2 text-gray-500">{s.created_at ? new Date(s.created_at).toLocaleString('en-AU') : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {drilldown && (
                <ChatbotSessionsDrilldownModal
                    title={drilldown.title}
                    sessions={drilldownSessions}
                    loading={drilldownLoading}
                    onClose={() => setDrilldown(null)}
                    onOpenProfile={openProfile}
                    redirecting={redirecting}
                    initialExpandedId={drilldown.initialExpandedId}
                />
            )}
        </div>
    );
};

export default ChatbotAnalyticsPanel;
